import { createOrmSelector } from './';
//import { createSelector } from 'reselect';

import { formatName } from '../common.js';
import {
  filter,
  flatMap,
  get,
  groupBy,
  keyBy,
  last,
  map,
  mapValues,
  max,
  maxBy,
  orderBy,
  sumBy,
} from 'lodash';

const selectModel = model =>
  model && { ...model.ref, primaryKey: model.primaryKey };

const genderSelector = gender => selectModel(gender);

const liftSelector = lift => selectModel(lift);

const bodyweightCategorySelector = bodyweightCategory => ({
  ...selectModel(bodyweightCategory),
  gender: genderSelector(bodyweightCategory.gender),
});

const ageGroupSelector = ageGroup => ({
  ...selectModel(ageGroup),
  gender: genderSelector(ageGroup.gender),
  bodyweightCategories: ageGroup.bodyweightCategories
    .toModelArray()
    .map(bodyweightCategorySelector),
});

const eventSelector = event => selectModel(event);

const contestSelector = contest => ({
  ...selectModel(contest),
  ageGroup: ageGroupSelector(contest.ageGroup),
  bodyweightCategory: bodyweightCategorySelector(contest.bodyweightCategory),
  entries: contest.entries.toModelArray().map(selectModel),
  groups: contest.groups.toModelArray().map(selectModel),
});

const teamSelector = team => selectModel(team);

const entrySelector = entry => {
  const contests = entry.contests
    .orderBy(['order'])
    .toModelArray()
    .map(contestSelector);
  const group = entry.group.first();
  const contest = group ? contestSelector(group.contest) : contests[0];
  const totalMinimum = max(map(contests, 'entryTotalMinimum'));

  return {
    ...selectModel(entry),
    serialized: entry.serialize(),
    name: formatName(entry),
    gender: genderSelector(entry.gender),
    lotNumber: get(entry.lot, 'number'),
    teams: entry.teams.toModelArray().map(teamSelector),
    contest,
    contests,
    referenceTotal: entry.total || totalMinimum || 0,
    totalMinimum,
    group: selectModel(group),
    weighInDatum: selectModel(entry.weighInDatum),
    // TODO: Key to weighInDeclarationsByLiftId already.
    weighInDeclarations: entry.weighInDeclarations
      .toModelArray()
      .map(selectModel),
  };
};
const entryOrder = [
  ['contest.bodyweightCategory.order', 'group.letter', 'lotNumber'],
  ['asc', 'desc', 'asc'],
];

const groupSelector = group => {
  const isOnlyGroup = group.contest.groups.count() <= 1;
  const entries = group.entries.toModelArray().map(entrySelector);

  return {
    ...selectModel(group),
    serialized: group.serialize(),
    isOnlyGroup,
    contest: contestSelector(group.contest),
    entries,
    session: selectModel(group.session.first()),
  };
};

const sessionSelector = session => {
  const groups = session.groups
    .orderBy(['order'])
    .toModelArray()
    .map(groupSelector);

  return {
    ...selectModel(session),
    groups,
    entries: orderBy(
      flatMap(groups, group =>
        map(group.entries, entry => ({ ...entry, group })),
      ),
      ...entryOrder,
    ),
  };
};

const weighInDatumSelector = weighInDatum => ({
  ...selectModel(weighInDatum),
  entry: entrySelector(weighInDatum.entry),
});

/*
const weighInDeclarationSelector = weighInDeclaration => ({
  ...selectModel(weighInDeclaration),
  entry: entrySelector(weighInDeclaration.entry),
  lift: liftSelector(weighInDeclaration.lift),
});
*/

const protocolSelector = protocol => ({
  ...selectModel(protocol),
  athletes: orderBy(protocol.athletes.toModelArray().map(athleteSelector), [
    'startNumber',
  ]),
});

const declarationOrder = [['attemptNumber', 'changeNumber']];

const attemptSelector = attempt => {
  const decisions = attempt.decisions.orderBy('order').toRefArray();

  return {
    ...selectModel(attempt),
    decision: last(decisions),
    decisions,
  };
};

const athleteSelector = athlete => {
  const declarations = orderBy(
    athlete.declarations.toModelArray().map(selectModel),
    ...declarationOrder,
  );
  const declarationsByLiftId = groupBy(declarations, 'lift');
  const lastDeclarationByLiftId = mapValues(
    declarationsByLiftId,
    declarations => last(declarations),
  );

  const attemptsByLiftId = groupBy(
    athlete.attempts.toModelArray().map(attemptSelector),
    'lift',
  );
  const lastAttemptByLiftId = mapValues(attemptsByLiftId, attempts =>
    maxBy(attempts, 'number'),
  );

  const bestAttemptByLiftId = mapValues(attemptsByLiftId, attempts =>
    maxBy(filter(attempts, { decision: { isGoodLift: true } }), 'weight'),
  );

  return {
    ...selectModel(athlete),
    entry: entrySelector(athlete.entry),
    declarationsByLiftId,
    lastDeclarationByLiftId,
    attemptsByLiftId,
    lastAttemptByLiftId,
    bestAttemptByLiftId,
    results: athlete.results.toModelArray().map(selectModel),
  };
};

const createGendersSelector = () =>
  createOrmSelector(({ Gender }) =>
    Gender.all()
      .toModelArray()
      .map(genderSelector),
  );

const createLiftsSelector = () =>
  createOrmSelector(({ Lift }) =>
    Lift.all()
      .toModelArray()
      .map(liftSelector),
  );

const createBodyweightCategoriesSelector = () =>
  createOrmSelector(({ BodyweightCategory }) =>
    BodyweightCategory.all()
      .toModelArray()
      .map(bodyweightCategorySelector),
  );

const createAgeGroupsSelector = () =>
  createOrmSelector(({ AgeGroup }) =>
    AgeGroup.all()
      .toModelArray()
      .map(ageGroupSelector),
  );

const createEventsSelector = () =>
  createOrmSelector(({ Event }) =>
    Event.all()
      .toModelArray()
      .map(eventSelector),
  );

const createContestsSelector = () =>
  createOrmSelector(({ Contest }) =>
    Contest.all()
      .toModelArray()
      .map(contestSelector),
  );

const createTeamsSelector = () =>
  createOrmSelector(({ Team }) =>
    Team.all()
      .toModelArray()
      .map(teamSelector),
  );

const createEntriesSelector = () =>
  createOrmSelector(({ Entry }) =>
    orderBy(
      Entry.all()
        .toModelArray()
        .map(entrySelector),
      ...entryOrder,
    ),
  );

const createGroupsSelector = () =>
  createOrmSelector(({ Group }) =>
    Group.all()
      .toModelArray()
      .map(groupSelector),
  );

const createSessionsSelector = () =>
  createOrmSelector(({ Session }) =>
    Session.all()
      .toModelArray()
      .map(sessionSelector),
  );

const createWeighInDatumsSelector = () =>
  createOrmSelector(({ WeighInDatum }) =>
    WeighInDatum.all()
      .toModelArray()
      .map(weighInDatumSelector),
  );

/*
const createWeighInDeclarationsSelector = () =>
  createOrmSelector(({ WeighInDeclaration }) =>
    WeighInDeclaration.all()
      .toModelArray()
      .map(weighInDeclarationSelector),
  );
*/

const createProtocolsSelector = () =>
  createOrmSelector(({ Protocol }) =>
    Protocol.all()
      .toModelArray()
      .map(protocolSelector),
  );

const createAthletesSelector = () =>
  createOrmSelector(({ Athlete }) =>
    Athlete.all()
      .toModelArray()
      .map(athleteSelector),
  );

const gendersSelector = createGendersSelector();
const liftsSelector = createLiftsSelector();
const bodyweightCategoriesSelector = createBodyweightCategoriesSelector();
const ageGroupsSelector = createAgeGroupsSelector();
const eventsSelector = createEventsSelector();
const contestsSelector = createContestsSelector();
const teamsSelector = createTeamsSelector();
const entriesSelector = createEntriesSelector();
const groupsSelector = createGroupsSelector();
const sessionsSelector = createSessionsSelector();
const protocolsSelector = createProtocolsSelector();
const athletesSelector = createAthletesSelector();
const weighInDatumsSelector = createWeighInDatumsSelector();

console.warn('TODO: Consolidate with ProtocolsDownloads!');
const resultsSelector = (athlete, { lifts }) => {
  const liftsById = keyBy(lifts, 'id');

  const { bestAttemptByLiftId } = athlete;

  const isLiftFinal = mapValues(liftsById, lift => {
    const lastDeclaration = athlete.lastDeclarationByLiftId[lift.id];
    const lastAttempt = athlete.lastAttemptByLiftId[lift.id];
    return get(lastDeclaration, 'attemptNumber') === get(lastAttempt, 'number');
  });
  const isTotalFinal =
    lifts.every(lift => isLiftFinal[lift.id]) ||
    lifts.some(lift => isLiftFinal[lift.id] && !bestAttemptByLiftId[lift.id]);
  const isTotalComplete = lifts.every(lift => bestAttemptByLiftId[lift.id]);
  const total = isTotalComplete
    ? sumBy(Object.values(bestAttemptByLiftId), 'weight')
    : isTotalFinal
    ? 0
    : undefined;

  return {
    resultsByLiftId: mapValues(liftsById, lift =>
      isLiftFinal[lift.id]
        ? get(bestAttemptByLiftId[lift.id], 'weight', 0)
        : undefined,
    ),
    // FIXME: This differs from protocol Final vs. Complete
    total,
  };
};

export {
  liftSelector,
  genderSelector,
  contestSelector,
  entrySelector,
  groupSelector,
  weighInDatumSelector,
  //weighInDeclarationSelector,
  protocolSelector,
  athleteSelector,
  /**/
  liftsSelector,
  gendersSelector,
  bodyweightCategoriesSelector,
  ageGroupsSelector,
  eventsSelector,
  contestsSelector,
  entriesSelector,
  teamsSelector,
  groupsSelector,
  sessionsSelector,
  protocolsSelector,
  athletesSelector,
  weighInDatumsSelector,
  resultsSelector,
  /**/
  createGendersSelector,
  createLiftsSelector,
  createBodyweightCategoriesSelector,
  createAgeGroupsSelector,
  createContestsSelector,
  createTeamsSelector,
  createEntriesSelector,
  createGroupsSelector,
  createSessionsSelector,
  createWeighInDatumsSelector,
  //createWeighInDeclarationsSelector,
  createProtocolsSelector,
};



// WEBPACK FOOTER //
// ./src/orm/selectors.js